import React from 'react'
import { Link } from 'react-router'
import {
  AlertTriangle,
  ExternalLink,
  Landmark,
  ShieldAlert,
} from 'lucide-react'
import type { Finding } from './networkText'

const ICON = {
  danger: <ShieldAlert className="h-4 w-4 text-signal" aria-hidden />,
  warning: <AlertTriangle className="h-4 w-4 text-signal-700" aria-hidden />,
  info: <Landmark className="h-4 w-4 text-ink-400" aria-hidden />,
}

/**
 * »Værd at vide«: one line per finding, with where it comes from and, for
 * registers matched on name only, how sure the match is. Hidden when there
 * is nothing to say.
 */
const WorthKnowing: React.FC<{
  items: Finding[]
  loadingSignals?: boolean
}> = ({ items, loadingSignals = false }) => {
  if (items.length === 0 && !loadingSignals) return null
  return (
    <section
      aria-labelledby="vaerd-at-vide"
      className="rounded-[10px] bg-paper-200 px-5 py-4"
      data-testid="worth-knowing"
    >
      <h2 id="vaerd-at-vide" className="text-[13px] font-semibold text-ink-500">
        Værd at vide
        {items.length > 0 && (
          <span className="tabular-nums"> · {items.length}</span>
        )}
        {loadingSignals && (
          <span className="font-normal"> · tjekker registre…</span>
        )}
      </h2>
      <ul className="mt-2 space-y-2">
        {items.map(f => (
          <li
            key={f.key}
            className="flex items-start gap-2.5 text-[14px] leading-snug text-ink"
          >
            <span className="mt-[2px] shrink-0">{ICON[f.tone]}</span>
            <span className="min-w-0">
              {f.href ? (
                <a
                  href={f.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline decoration-ink-300 underline-offset-2"
                >
                  {f.text}
                  <ExternalLink
                    className="ml-1 inline h-3 w-3 align-[-1px] text-ink-400"
                    aria-hidden
                  />
                </a>
              ) : (
                <LinkedText text={f.text} links={f.links ?? []} />
              )}
              <span className="ml-1.5 whitespace-nowrap text-[12px] text-ink-400">
                {f.source}
                {f.match === 'name' && ' · navnematch'}
                {f.match === 'short' &&
                  ' · muligt navnematch, mellemnavn afviger'}
              </span>
            </span>
          </li>
        ))}
      </ul>
      {items.some(f => f.match) && (
        <p className="mt-3 text-[12px] text-ink-400">
          Registrene uden for CVR deler ikke id med CVR, så fund matcher på
          navn. Tjek, at det er samme person, før du bruger det.
        </p>
      )}
    </section>
  )
}

/** The sentence with every name that has a page turned into a link. */
function LinkedText({
  text,
  links,
}: {
  text: string
  links: Array<{ label: string; to: string }>
}) {
  const parts: React.ReactNode[] = []
  let rest = text
  const sorted = [...links].sort((a, b) => b.label.length - a.label.length)
  while (rest) {
    let hit: { at: number; link: { label: string; to: string } } | null = null
    for (const link of sorted) {
      const at = rest.indexOf(link.label)
      if (at >= 0 && (hit === null || at < hit.at)) hit = { at, link }
    }
    if (!hit) {
      parts.push(rest)
      break
    }
    if (hit.at > 0) parts.push(rest.slice(0, hit.at))
    parts.push(
      <Link
        key={parts.length}
        to={hit.link.to}
        className="font-semibold hover:underline decoration-ink-300 underline-offset-2"
      >
        {hit.link.label}
      </Link>
    )
    rest = rest.slice(hit.at + hit.link.label.length)
  }
  return <>{parts}</>
}

export default WorthKnowing
